import React, { useEffect, useState } from 'react';
import { useLocation } from 'wouter';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Gamepad, Users, Sparkles } from 'lucide-react';
import { apiRequest } from '@/lib/queryClient';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/use-auth';
import { GameExplanationDialog } from './GameExplanationDialog';
import { useDefaultDeck } from '@/hooks/use-default-deck';

// Constants for local storage keys and default card deck URL
const TUTORIAL_SUGGESTION_KEY = 'spar-tutorial-suggestion-shown';
const TUTORIAL_LAST_USER_KEY = 'spar-tutorial-last-user';
const DEFAULT_DECK_URL = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/deck/free/default';
// App logo URL
const APP_LOGO_URL = 'https://teacherwilliam.sgp1.cdn.digitaloceanspaces.com/spar/spar-legends.jpeg';

export function TutorialSuggestionDialog() {
  const [isOpen, setIsOpen] = useState(false);
  const [showExplanation, setShowExplanation] = useState(false);
  const [_, setLocation] = useLocation();
  const { toast } = useToast();
  const { user } = useAuth();
  const [shouldCheckAPI, setShouldCheckAPI] = useState(false);
  
  // Use the direct default URL instead of attempting to fetch it
  const defaultDeckUrl = DEFAULT_DECK_URL;
  
  // First check localStorage when component loads
  useEffect(() => {
    if (!user) return;
    
    console.log('Tutorial debug - Checking localStorage first');
    const tutorialCompleted = localStorage.getItem(TUTORIAL_SUGGESTION_KEY);
    
    // If localStorage indicates the tutorial is complete, do nothing
    if (tutorialCompleted === 'true') {
      console.log('Tutorial debug - Tutorial marked as completed in localStorage');
      return;
    }
    
    // If localStorage doesn't indicate completion, we need to check the API
    console.log('Tutorial debug - Tutorial not marked as completed in localStorage, will check API');
    setShouldCheckAPI(true);
  }, [user]);
  
  // Create practice game mutation
  const createPracticeGame = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/game-rooms/practice');
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to create practice game');
      }
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: 'Practice Game Created',
        description: 'Starting tutorial match against AI',
      });
      setLocation(`/game/${data.id}`);
    },
    onError: (error: Error) => {
      toast({
        title: 'Error Creating Game',
        description: error.message || 'Failed to create practice game',
        variant: 'destructive',
      });
    },
  });
  
  // Mutation to save tutorial completion status
  const saveTutorialStatus = useMutation({
    mutationFn: async () => {
      const res = await apiRequest('POST', '/api/profile/tutorial-complete', {
        completed: true
      });
      
      if (!res.ok) {
        const error = await res.json();
        throw new Error(error.message || 'Failed to save tutorial status');
      }
      return res.json();
    },
    onError: (error: Error) => {
      // Just log the error but don't show to user as this is non-critical
      console.error("Failed to save tutorial status:", error);
    }
  });
  
  // Query to check if user has completed tutorial - ONLY runs if shouldCheckAPI is true
  const { data: tutorialStatus, isLoading: loadingTutorialStatus } = useQuery({
    queryKey: ['tutorial-status', user?.id],
    queryFn: async () => {
      console.log('Tutorial debug - Making API call to check tutorial status');
      const res = await apiRequest('GET', `/api/profile/status/${user?.id}`);
      
      if (!res.ok) throw new Error("tutorial status fetch failed");
      
      const data = await res.json();
      console.log('Fetched tutorial status:', data);
      return data;
    },
    enabled: !!user && shouldCheckAPI, // Only run the query if we need to check the API
  });

  // Handle the API response if we made the call
  useEffect(() => {
    // Only proceed if we're checking the API and have a response
    if (!shouldCheckAPI || loadingTutorialStatus || !user) return;
    
    console.log('Tutorial debug - Processing API response:', tutorialStatus);
    try {
      // If the API indicates the user completed the tutorial, update localStorage
      if (tutorialStatus?.completed) {
        console.log('Tutorial debug - API shows tutorial completed');
        localStorage.setItem(TUTORIAL_SUGGESTION_KEY, 'true');
        return;
      }

      // If the API says the tutorial is not completed, show the dialog
      console.log('Tutorial debug - API shows tutorial not completed, showing dialog');
      const timer = setTimeout(() => {
        console.log('Tutorial debug - Timeout triggered, setting isOpen to true');
        setIsOpen(true);
      }, 1500);
      
      return () => clearTimeout(timer);
    } catch (error) {
      console.error('Error handling tutorial dialog logic:', error);
    }
  }, [user, tutorialStatus, loadingTutorialStatus, shouldCheckAPI]);

  const handleStartTutorial = () => {
    // Show the explanation dialog instead of immediately creating a practice game
    setShowExplanation(true);
    setIsOpen(false);
  };

  const handleStartPracticeMatch = () => {
    // Only update localStorage when user completes the tutorial
    localStorage.setItem(TUTORIAL_SUGGESTION_KEY, 'true');
    
    // Save tutorial completion status to the database
    saveTutorialStatus.mutate();
    
    // Close the explanation dialog first
    setShowExplanation(false);
    
    // Create a practice game against AI
    createPracticeGame.mutate();
  };

  const handleClose = () => {
    // Only update localStorage when user skips the tutorial
    localStorage.setItem(TUTORIAL_SUGGESTION_KEY, 'true');
    
    // Save tutorial completion status to the database (as skipped)
    saveTutorialStatus.mutate();
    
    setIsOpen(false);
  };

  const handleSkipExplanation = () => {
    // Only update localStorage when user skips the tutorial
    localStorage.setItem(TUTORIAL_SUGGESTION_KEY, 'true');
    
    // Save tutorial completion status to the database (as skipped)
    saveTutorialStatus.mutate();
    
    setShowExplanation(false);
  };

  return (
    <>
      {/* Initial welcome dialog */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-black/95 border border-primary/30 shadow-lg shadow-primary/5">
          <DialogHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-primary" />
              <DialogTitle className="text-xl text-white">Welcome to Spar Legends!</DialogTitle>
            </div>
            <DialogDescription className="text-gray-300 mt-2">
              Want to learn how to play with a quick tutorial match?
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="flex items-center justify-center bg-black/30 p-6 rounded-lg border border-primary/20 mb-4">
              <div className="flex flex-col items-center">
                <div className="w-16 h-16 rounded-full overflow-hidden border-2 border-primary/50 shadow-glow">
                  <img 
                    src={APP_LOGO_URL} 
                    alt="Spar Legends" 
                    className="w-full h-full object-cover"
                  />
                </div>
                <p className="text-center text-white mt-2">
                  Start with a tutorial to learn the game rules before playing a practice match.
                </p>
              </div>
            </div>
          </div>

          <DialogFooter className="flex gap-3 pt-2">
            <Button
              variant="outline"
              onClick={handleClose}
              className="flex-1 border-gray-700 text-gray-300 hover:bg-gray-900"
            >
              <Users className="h-4 w-4 mr-2" />
              <span>Skip for now</span>
            </Button>
            <Button 
              onClick={handleStartTutorial} 
              className="flex-1 bg-primary hover:bg-primary/90"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              <span>Learn to Play</span>
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Game explanation dialog with multi-page tutorial */}
      <GameExplanationDialog
        isOpen={showExplanation}
        onOpenChange={setShowExplanation}
        onStartPractice={handleStartPracticeMatch}
        onSkip={handleSkipExplanation}
        defaultDeckUrl={defaultDeckUrl}
      />
    </>
  );
}